<script lang="ts">import { createEventDispatcher, onDestroy } from 'svelte';
import MagnifyingGlass from './MagnifyingGlass.svelte';
import XCircle from './XCircle.svelte';
import { getSearchOptionsStore } from './SearchProvider.svelte';
import { getTracking } from '../../js-v2/tracking';
import { getNavDeviceType } from '../../js-v2/trackingUtils';
import { getUrlPath } from '../../js-v2/urlUtils';
export let inputEl;
let closeButtonElement;
let value = '';
const searchOptions = getSearchOptionsStore();
const trackFn = getTracking();
const dispatch = createEventDispatcher();
let debounceTimeout;
function handleFocus() {
    trackFn({
        eventName: 'Click Search Bar',
        properties: {
            currentPage: getUrlPath(),
            navDeviceType: getNavDeviceType(inputEl),
        },
    });
}
function handleInput() {
    window.clearTimeout(debounceTimeout);
    debounceTimeout = window.setTimeout(() => {
        if (value.length > 0) {
            trackFn({
                eventName: 'Type In Search Bar',
                properties: {
                    searchQuery: value,
                    currentPage: getUrlPath(),
                    navDeviceType: getNavDeviceType(inputEl),
                },
            });
        }
    }, 400);
}
function handleClearButtonClick() {
    value = '';
    inputEl.focus();
}
function handleFormSubmit(event) {
    if (!value) {
        event.preventDefault();
    }
}
function handleInputBlur(event) {
    if (event.relatedTarget !== closeButtonElement) {
        dispatch('blur');
    }
}
function handleCloseButtonBlur(event) {
    if (event.relatedTarget !== inputEl) {
        dispatch('blur');
    }
}
onDestroy(() => {
    if (typeof window !== 'undefined') {
        window.clearTimeout(debounceTimeout);
    }
});
</script>

<form action={$searchOptions.searchPageUrl} class:empty={value.length === 0} method="get" on:submit={handleFormSubmit}>
  <MagnifyingGlass />

  <input
    name="query"
    bind:this={inputEl}
    bind:value
    placeholder="Search squareup.com"
    on:focus={handleFocus}
    on:blur={handleInputBlur}
    on:input={handleInput}
  />

  <button
    bind:this={closeButtonElement}
    type="button"
    aria-label="clear search input"
    on:click={handleClearButtonClick}
    on:blur={handleCloseButtonBlur}
  >
    <XCircle />
  </button>
</form>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
form.empty {
  color: #767676;
}
form.empty button {
  display: none;
}
form:focus-within {
  color: var(--color-text, var(--color-neutral-000, #000000));
}
@media (min-width: 64rem) {
  form {
    padding: 40px 88px;
  }
}

input {
  flex-grow: 1;
  font-weight: 500;
  font-size: 24px;
  line-height: 30.58px;
  letter-spacing: -0.03em;
  background: transparent;
  color: var(--color-text);
  font-family: var(--text-font-family);
  border: none;
  outline: none;
}

button {
  min-height: unset !important;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}</style>
